import axios from 'axios';
import * as moment from 'moment';
import memoize = require('lodash/memoize');
import { ICalendarAppBaseText } from '../classes/buytickets-text';
import { IBuyTicketsState, IBuyTicketsSettings, IOption } from '../classes/buytickets';
import { IEvent } from '../classes/event';
import { IShowPosterInfo } from '../classes/showPosterInfo';

declare var productionSeasonId: string;//Comes from the view BuyTicketsPage.cshtml

export default class CalendarApi {
    private static apiDateFormat = "YYYY-MM-DD";
    private static aceCalendarAPIRoute = "ace-api/events/buyticketsfilter";
    private static buyTicketsAPIRoute = "ace-api/events/buytickets?productionSeasonId=";
    private static showPosterAPIRoute = "ace-api/events/showListingItem?productionSeasonId=";
    private static buyTicketsEventCache = memoize(CalendarApi.getBuyTicketsEventsWrapper, (state: IBuyTicketsState<IEvent>, settings: IBuyTicketsSettings) => `${state.startDate.format('YYYY-MM-DD')}~${state.endDate.format('YYYY-MM-DD')}~${state.productionSeasonFilter.toString()}`);
    private static eventsCache = memoize(CalendarApi.getEventsWrapper, (state: IBuyTicketsState<IEvent>, settings: IBuyTicketsSettings) => `${state.startDate.format('YYYY-MM-DD')}~${state.endDate.format('YYYY-MM-DD')}~${state.productionSeasonFilter.toString()}`);
    private static textCache = memoize(CalendarApi.getTextFromApi, (pageId: number, culture: string) => `${pageId}${culture}`);
    private static filtersSource = axios.CancelToken.source();

    private static getFiltersApi(filter: string): Promise<IOption[]> {
        var requestString: string = '/';
        requestString = `${requestString}${this.aceCalendarAPIRoute}?name=${filter}&productionSeasonId=${productionSeasonId}`;

        return axios.get<IOption[]>(requestString, {
            withCredentials: true,
            cancelToken: this.filtersSource.token
        }).then(response => response.data);
    }

    /**
     * Call the API to receive a more specific set of filters.
     * @param filter
     * @param culture
     */
    public static getFiltersFor(filter: string): Promise<IOption[]> {
        return this.getFiltersApi(filter);
    }

    /**
     * Invoke this to cancel all outstanding requests to retrieve filters.
     * @param reason
     */
    public static cancelFiltersRequest(reason = "Filters API request manually canceled.") {
        this.filtersSource.cancel(reason);
    }

    /**
     * Wrapper function to automatically determine which API call to make.
     * @param state The calendar state.
     * @param settings The settings of the current calendar.
     */
    private static getEventsWrapper(state: IBuyTicketsState<IEvent>, settings: IBuyTicketsSettings) {
        const startDate = settings.loadWrappingMonthDates ? state.startDate.startOf("week").format(this.apiDateFormat) : state.startDate.format(this.apiDateFormat);
        const endDate = settings.loadWrappingMonthDates ? state.endDate.startOf("week").format(this.apiDateFormat) : state.endDate.format(this.apiDateFormat);

        if (state.productionSeasonFilter > 0) {
            return this.getProductionEventsForDates("", state.productionSeasonFilter, startDate, endDate);
        } else {
            return this.getEventsForDates("", startDate, endDate);
        }
    }

    /**
     * Wrapper function to automatically determine which API call to make.
     * @param state The calendar state.
     * @param settings The settings of the current calendar.
     */
    private static getBuyTicketsEventsWrapper(state: IBuyTicketsState<IEvent>, settings: IBuyTicketsSettings) {
        return this.getBuyTicketsEventsForDates();
    }

    private static getBuyTicketsShowPosterInfo() {
        return this.getShowPosterInfo();
    }

    /**
     * When grabbing all events between two dates from the API.
     * @param culture
     * @param startDate
     * @param endDate
     */
    private static getEventsForDates(culture: string, startDate: string, endDate: string): Promise<IEvent[]> {
        var requestString: string = '/';
        if (culture.length > 0)
            requestString += culture + '/';
        requestString = requestString + this.aceCalendarAPIRoute + "?startDate=" + startDate + "&endDate=" + endDate;

        return axios.get<IEvent[]>(requestString, {
            withCredentials: true
        }).then(response => {
            return response.data;
        });
    }

    private static getBuyTicketsEventsForDates(): Promise<IEvent[]> {
        var requestString: string = '/';
        requestString = requestString + this.buyTicketsAPIRoute + productionSeasonId;
        return axios.get<IEvent[]>(requestString, {
            withCredentials: true
        }).then(response => {
            return response.data;
        });
    }

    public static getShowPosterInfo(): Promise<IShowPosterInfo> {
        var requestString: string = '/';
        requestString = requestString + this.showPosterAPIRoute + productionSeasonId;
        return axios.get<IShowPosterInfo>(requestString, {
            withCredentials: true
        }).then(response => {
            return response.data;
        });
    }

    /**
     * When grabbing events for a specific production ID. This doesn't seem to be used as frequently anymore.
     * @param culture
     * @param productionSeasonId
     * @param startDate
     * @param endDate
     */
    private static getProductionEventsForDates(culture: string, productionSeasonId: number, startDate: string, endDate: string): Promise<IEvent[]> {
        var requestString: string = '/';

        if (culture.length > 0)
            requestString += culture + '/';
        requestString = requestString + this.aceCalendarAPIRoute + "/" + productionSeasonId.toString() + "/" + "?startDate=" + startDate + "&endDate=" + endDate;

        return axios.get<IEvent[]>(requestString, {
            withCredentials: true
        }).then(response => response.data);
    }

    /**
     * Call this to clear the memoized events and start fresh.
     */
    public static clearEventsCache(): void {
        return this.eventsCache.cache.clear();
    }

    /**
     * The public facing API to retrieve events. Set bustCache to true if you wish to override cached values.
     * @param state The calendar's current state.
     * @param settings The settings utilized by the calendar.
     * @param bustCache Defaults to false, Set to true to force an API call.
     */
    public static getEvents(state: IBuyTicketsState<IEvent>, settings: IBuyTicketsSettings, bustCache = false): Promise<IEvent[]> {
        if (bustCache) {
            this.buyTicketsEventCache.cache.set(`${state.startDate.format('YYYY-MM-DD')}~${state.endDate.format('YYYY-MM-DD')}~${state.productionSeasonFilter.toString()}`, this.getEventsWrapper(state, settings));
        }

       // return this.eventsCache(state, settings);
        return this.buyTicketsEventCache(state, settings);
    }
    
    /**
     * Grab text data from the API.
     * @param pageId
     * @param culture
     */
    private static getTextFromApi(pageId: number, culture: string): Promise<ICalendarAppBaseText> {
        var requestString: string = '/';

        if (culture.length > 0)
            requestString += culture + '/';
        requestString = `${requestString}${this.aceCalendarAPIRoute}/text?culture=${culture}&pageId=${pageId}`;

        return axios.get<ICalendarAppBaseText>(requestString, {
            withCredentials: true,
        }).then(response => response.data);
    }

    /**
     * Public facing API to obtain the text for a relevant culture on the current page.
     * @param culture
     */
    public static getText(culture: string, bustCache = false): Promise<ICalendarAppBaseText> {
        if (!pageId) {
            var pageId = 0;
        }

        if (bustCache) {
            this.clearTextCache();
        }

        return this.textCache(pageId, culture);
    }

    /**
     * Clear the cache containing the text API information.
     */
    public static clearTextCache() {
        this.textCache.cache.clear();
    }
}
